import React, { useState, useCallback } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { GenericModal } from '..';
import { WelcomePopupModalProps } from './types';
import CompleteProfileModal from './complete-profile-modal.component';
import BusinessProfileModal from './business-profile-modal.component';
import './welcome-popup-modal.style.scss';
import WelcomeCuate1 from '../../../../../public/assets/svg/Welcome-cuate-1.svg';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/useRedux';
import { updateUserOnboardingService } from '../../../features/userOnboarding/userOnboarding.slice';
import { user } from '../../images/jpg/healthInsuranceIcons';

type ModalStep = 'welcome' | 'profile' | 'closed';

const WelcomePopupModal: React.FC<WelcomePopupModalProps> = ({
  show,
  userType,
  handleStartTour,
  close,
  setShowWelcomeModal
}) => {
  const [currentStep, setCurrentStep] = useState<ModalStep>('welcome');
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Step 1: Mark onboarding as complete and close everything
  const completeOnboarding = useCallback(async () => {
    try {
      await dispatch(updateUserOnboardingService({ actionType: 'IsCloseSteps', value: true }));
    } catch (error) {
      console.error('Failed to update onboarding service:', error);
    }
  }, [dispatch]);

  // Step 2: Close all modals completely
  const closeAllModals = useCallback(async () => {
    await completeOnboarding();
    setCurrentStep('closed');
    setShowWelcomeModal(false);
    close();
  }, [completeOnboarding, close, setShowWelcomeModal]);

  // Step 3: Handle welcome modal close button
  const handleWelcomeClose = useCallback(() => {
    closeAllModals();
  }, [closeAllModals]);

  // Step 4: Handle welcome modal next button - go to profile step
  const handleWelcomeNext = useCallback(() => {
    setCurrentStep('profile');
  }, []);

  const handleProfileClose = useCallback(() => {
    setCurrentStep('welcome');
  }, []);

  const handleProfileNext = useCallback(() => {
    closeAllModals();
  }, [closeAllModals]);

  const handleStartTourClick = useCallback(() => {
    closeAllModals();
    if (handleStartTour) {
      handleStartTour();
    }
  }, [closeAllModals, handleStartTour]);

  const isWelcomeStep = currentStep === 'welcome';
  const isProfileStep = currentStep === 'profile';
  return (
    <>
      {/* Step 1: Welcome Modal */}
      <div className='welcome-popup-container-main'>
        <GenericModal
          show={show && isWelcomeStep}
          onClose={handleWelcomeClose}
          size="lg"
          showFooter={false}
          showCloseButton={false}
          closeOnBackdrop={true}
          className="welcome-popup-modal"
        >
          <div className="welcome-popup-content">
            <div className="welcome-popup-header">
              <button
                type="button"
                className="welcome-popup-close-btn"
                onClick={handleWelcomeClose}
                aria-label="Close"
              >
                <AiOutlineClose size={32} />
              </button>
            </div>

            <div className="welcome-popup-body">
              <div className="welcome-illustration">
                <div className="celebration-scene">
                  <img
                    src={WelcomeCuate1}
                    alt="Welcome Illustration"
                    className="welcome-image"
                  />
                </div>
                <div className="confetti">
                  {Array.from({ length: 20 }).map((_, index) => (
                    <div key={index} className={`confetti-piece piece-${index + 1}`}></div>
                  ))}
                </div>
              </div>

              <div className="welcome-text-content">
                <h1 className="welcome-title">
                  {t('WELCOME_ON_BOARD')}
                </h1>
                <p className="welcome-description">
                  {t('THANK_YOU_FOR_TRUST')}
                </p>
              </div>
            </div>

            <div className="welcome-popup-footer">
              <div className="progress-dots">
                <div className="dot active"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>

              <button
                type="button"
                className="welcome-next-btn"
                onClick={handleWelcomeNext}
              >
                {t('NEXT_STEP')}
              </button>
            </div>
          </div>
        </GenericModal>
      </div>

      {Number(userType) === 1 &&
        <CompleteProfileModal
          onStartTour={handleStartTourClick}
          show={show && isProfileStep}
          onClose={handleProfileClose}
          onNext={handleProfileNext}
        />
      }
      {Number(userType) === 2 && (
      <BusinessProfileModal
      onStartTour={handleStartTourClick}
        show={show && isProfileStep}
        onClose={handleProfileClose}
        onNext={handleProfileNext}
      />
      )}
    </>
  );
};

export default WelcomePopupModal;
